<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useGridRef, useRouteColumnFilter, useODataSource, useLookup, useDefaultGridOptions, useDefaultEditing, refreshGridAsync, getCreatedRowId, refreshChangedRowAsync } from '@/composables/data-grid'
import { useEntityChanges } from '@/composables/entity-changes'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useArchive } from '@/composables/archive'
import { useChips } from '@/composables/chips'
import { useRoiCompareBasket } from '@/composables/roi-comparison'

import { planningApi } from '@/apis/planning.api'
import { usePortalStore, portalAccessStatusEnum, entityChangeTypeEnum } from '@/stores/portal.store'
import { usePlanningStore } from '@/stores/planning.store'

import ArchiveGridButton from '@/components/shared/ArchiveGridButton.vue'
import ArchivedGridToggle from '@/components/shared/ArchivedGridToggle.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import PortalAccessStatusChip from '@/components/shared/PortalAccessStatusChip.vue'
import TagCellEditTemplate from '@/components/shared/TagCellEditTemplate.vue'
import FileViewer from '@/components/shared/FileViewer.vue'
import StaffEdit from '@/components/shared/StaffEdit.vue'
import StaffsGridDetail from '@/components/planner/StaffsGridDetail.vue'
import GroupLinksTemplate from '@/components/shared/GroupLinksTemplate.vue'
import ServiceImport from '@/components/shared/ServiceImport.vue'
import RoiComparisonBasket from '@/components/roi/RoiComparisonBasket.vue'
import { useWebSiteStore } from '@/stores/web-site.store'

const gridId = 'staffs-grid-v2'

const i18n = getI18n()
const portalStore = usePortalStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const canManageStaff = webSiteStore.can(webSiteStore.permissionFlagEnum.MASTER_DATA)

planningStore.fetchGroupsAsync({ all: true }, true)

const localizedServiceAvailabilityTypes = portalStore.getLocalizedServiceAvailabilityTypes()
const drivingLicenseNames = Object.fromEntries(portalStore.getLocalizedDrivingLicenseTypes().map(l => [l.id, l.name]))
const localizedUserRoles = webSiteStore.getLocalizedRoles()
const localizedPortalAccessStatuses = portalStore.getLocalizedPortalAccessStatuses()
const onResendInvite = async (staff) => {
  await planningStore.resendInviteStaffAsync(staff.id)
  await gridRef.value?.instance?.refresh()
}
const localizedLanguages = portalStore.getLocalizedLanguages()
const localizedServiceTags = planningStore.getLocalizedServiceTags()
const getServiceTags = () => localizedServiceTags
const { getServiceTagChips } = useChips()
const editItem = ref(null)
const quickCreate = ref(false)
const onAddClick = (quick = false) => { quickCreate.value = quick === true; editItem.value = {} }
const showImport = ref(false)

const { gridRef } = useGridRef()
const basket = useRoiCompareBasket(planningStore.roiComparisonTypeEnum.STAFF, gridRef)
const { onContentReady } = useRouteColumnFilter(gridRef)
const servicePresentationExport = {
  label: i18n.t('common.staff'),
  onClick: (selectedRowsData) => planningStore.exportServicesExcelAsync(portalStore.serviceTypeEnum.STAFF, selectedRowsData.map(row => row.id)),
}

const gridDataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.staffsUrl, {
  customParamsFn: () => { return showArchived.value ? { show_archived: true } : {} }
})
const {
  archiveWarning, archiveButton, showArchived, onRowPrepared: onArchiveRowPrepared, onRowRemoving,
  deleteSelectedToolbarItem, onDeleteSelectionChanged, onArchiveWarningConfirm,
} = useArchive({
  onArchive: async(id) => { await planningStore.archiveStaffAsync(id, { skipErrorMessage: true }) },
  onDelete: async(id, config) => { await planningStore.deleteStaffAsync(id, config) },
  onBulkDelete: async(data, config) => { await planningStore.bulkDeleteServicesAsync(portalStore.serviceTypeEnum.STAFF, data, config) },
  gridRef: gridRef,
})

const columns = [
  {
    type: 'buttons',
    showInColumnChooser: false,
    buttons: [
      { 
        name: 'edit',
        visible: (e) => !e.row?.data?.archived && canManageStaff,
        onClick: (e) => { editItem.value = { ...e.row.data } },
      },
      { name: 'delete', visible: (e) => !e.row?.data?.archived && canManageStaff },
      archiveButton,
    ],
    fixed: false,
    visibleIndex: -1,
    width: '6rem',
  },
  dataGridColumn.useAvatarColumn(),
  { dataField: 'code', caption: i18n.t('common.code'), width: '6rem', sortOrder: 'asc' },
  {
    dataField: 'name',
    caption: i18n.t('common.nameExt'),
    validationRules: [{ type: 'required' }],
    width: '12rem',
  },
  {
    dataField: 'first_name',
    caption: i18n.t('common.name'),
    validationRules: [{ type: 'required' }],
    sortOrder: 'asc'
  },
  {
    dataField: 'email',
    caption: i18n.t('common.email'),
    validationRules: [
      { type: 'email' },
      { type: 'custom', message: i18n.t('error.validation.emailRequiredForPortalAccess'), reevaluate: true, validationCallback: (e) => {
        return e.value || !e.data.role
      }}
    ]
  },
  {
    dataField: 'phone',
    caption: i18n.t('common.phone'),
    validationRules: [{ type: 'required' }],
  },
  dataGridColumn.useWarehouseColumn({
    dataField: 'warehouse',
    caption: i18n.t('common.workplace'),
    allowEditing: false,
  }),
  dataGridColumn.useGroupsColumn(),
  dataGridColumn.useServiceTagsColumn({
    dataSource: getServiceTags(),
    getTagsFn: getServiceTags,
    cellTemplate: 'service-tags-cell-template',
    editCellTemplate: 'service-tags-cell-edit-template',
  }),
  {
    dataField: 'availability_type',
    caption: i18n.t('common.availabilityType'),
    lookup: useLookup({
      dataSource: localizedServiceAvailabilityTypes,
      valueExpr: 'id',
      displayExpr: 'name',
    }),
    cellTemplate: 'availability-type-cell-template',
    validationRules: [{ type: 'required' }],
  },
  {
    dataField: 'vat_number',
    caption: i18n.t('common.vatNumber'),
    visible: false,
  },
  {
    dataField: 'driving_licenses',
    caption: i18n.t('common.drivingLicense'),
    calculateCellValue: (row) => (row.driving_licenses || []).map(id => drivingLicenseNames[id]).join(', '),
    allowSorting: false,
    allowFiltering: false,
    allowHeaderFiltering: false,
    visible: false,
  },
  {
    dataField: 'iban',
    caption: i18n.t('common.iban'),
    visible: false,
  },
  {
    dataField: 'service_files',
    caption: i18n.t('common.attachment', 2),
    cellTemplate: 'fileViewer',
    width: '16rem',
    allowExporting: false,
  },
  {
    dataField: 'role',
    caption: i18n.t('common.role'),
    lookup: useLookup({
      dataSource: localizedUserRoles,
      valueExpr: 'id',
      displayExpr: 'name',
    }),
    validationRules: [
      { type: 'custom', message: i18n.t('error.validation.roleRequiredForPortalAccess'), reevaluate: true, validationCallback: (e) => {
        return e.value || !e.data.preferred_language
      }}
    ],
    cellTemplate: 'role-cell-template',
  },
  {
    dataField: 'portal_access_status',
    caption: i18n.t('common.portalAccessStatus'),
    allowEditing: false,
    lookup: useLookup({
      dataSource: localizedPortalAccessStatuses,
      valueExpr: 'id',
      displayExpr: 'name',
    }),
    cellTemplate: 'portal-access-status-cell-template',
  },
  {
    dataField: 'preferred_language',
    caption: i18n.t('common.preferredLanguage'),
    lookup: useLookup({
      dataSource: localizedLanguages.map(l => ({ id: l.code, name: l.name })),
      valueExpr: 'id',
      displayExpr: 'name',
    }),
    validationRules: [
      { type: 'custom', message: i18n.t('error.validation.emailRequiredForPortalAccess'), reevaluate: true, validationCallback: (e) => {
        return e.value || !e.data.role
      }}
    ]
  },
  ...dataGridColumn.useRateCardColumns(),
  dataGridColumn.useArchivedAtColumn(),
  dataGridColumn.useArchivedColumn(),
]

const gridOptions = useDefaultGridOptions({
  id: gridId,
  columns: columns,
  dataSource: gridDataSource,
  editing: useDefaultEditing(null, null,{
    allowAdding: canManageStaff,
    allowUpdating: canManageStaff,
    allowDeleting: canManageStaff,
  }),
  masterDetail: {
    enabled: true,
    template: 'master-detail',
  },
  onRowPrepared: (e) => { onArchiveRowPrepared(e); basket.onRowPrepared(e) },
  onRowRemoving: onRowRemoving,
  onContentReady: onContentReady,
  exportFileName: i18n.t('common.staff'),
  presentationExports: [servicePresentationExport],
  selection: { mode: 'multiple', showCheckBoxesMode: 'onClick', selectAllMode: 'page' },
  onSelectionChanged: (e) => { onDeleteSelectionChanged(e); basket.onSelectionChanged(e) },
  dataActionWidgets: [deleteSelectedToolbarItem],
  extraToolbarItems: [basket.toolbarItem],
  onImport: canManageStaff ? () => { showImport.value = true } : undefined,
  clearFilterButton: true,
  archivedToggle: true,
  addButtons: [{
    icon: 'mdi-flash',
    hint: i18n.t('common.quickCreate'),
    visible: canManageStaff,
    onClick: () => onAddClick(true),
  }],
  addButton: { visible: canManageStaff, onClick: () => onAddClick(false) },
})

const onImported = async() => {
  showImport.value = false
  await gridRef.value.instance.refresh()
}

const updateGrid = async (saved) => {
  const createdId = getCreatedRowId(editItem.value, saved)
  editItem.value = null
  await refreshGridAsync(gridRef, createdId)
}


useEntityChanges([entityChangeTypeEnum.STAFF], (change) => refreshChangedRowAsync(gridRef, change), () => editItem.value !== null)
</script>
<template>
  <div>
    <q-dialog :model-value="editItem !== null" class="dialog-sm" @hide="updateGrid">
      <staff-edit
        v-if="editItem !== null"
        :model-value="editItem"
        :initial-quick-create="quickCreate"
        @update="updateGrid"
        @cancel="updateGrid"
        @delete="updateGrid"
      />
    </q-dialog>
    <q-dialog v-model="showImport" persistent class="dialog-lg">
      <service-import
        v-if="showImport"
        :service-type="portalStore.serviceTypeEnum.STAFF"
        @update="onImported"
        @cancel="showImport = false"
      />
    </q-dialog>
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="onArchiveWarningConfirm"
    />
    <roi-comparison-basket :basket="basket" />
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #master-detail="{ data: templateData }">
        <staffs-grid-detail :staff="templateData.data" />
      </template>
      <template #fileViewer="{ data: templateOptions }">
        <file-viewer
          dense
          :cell-info="templateOptions"
        />
      </template>
      <template #role-cell-template="{ data: templateOptions }">
        <chip-template :values="localizedUserRoles.filter(s => s.id === templateOptions.data.role)" />
      </template>
      <template #portal-access-status-cell-template="{ data: templateOptions }">
        <portal-access-status-chip
          :status="templateOptions.data.portal_access_status"
          :resendable="templateOptions.data.portal_access_status === portalAccessStatusEnum.EXPIRED && webSiteStore.can(webSiteStore.permissionFlagEnum.USERS_PERMISSIONS)"
          @resend="onResendInvite(templateOptions.data)"
        />
      </template>
      <template #groups-cell-template="{ data: templateOptions }">
        <group-links-template :group-ids="templateOptions.data.groups" />
      </template>
      <template #service-tags-cell-template="{ data: templateOptions }">
        <chip-template
          v-if="templateOptions.data.tags?.length"
          :values="getServiceTagChips(templateOptions.data.tags)"
        />
      </template>
      <template #service-tags-cell-edit-template="{ data: templateOptions }">
        <tag-cell-edit-template
          :value="templateOptions.data.tags"
          :data-source="getServiceTags()"
          @value-changed="(e) => templateOptions.setValue(e.value)"
        />
      </template>
      <template #availability-type-cell-template="{ data: templateOptions }">
        <chip-template :values="localizedServiceAvailabilityTypes.filter(t => t.id == templateOptions.data.availability_type)" />
      </template>
      <template #archive-button="{ data: row }">
        <archive-grid-button :row="row" />
      </template>
      <template #archived-toggle>
        <archived-grid-toggle
          :show-archived="showArchived"
          :grid-ref="gridRef"
          @update:show-archived="showArchived = $event"
        />
      </template>
    </dx-data-grid>
  </div>
</template>